import { YEventSource, YEventHandlers, YInputEvent, YStateEvent, YValueEvent, $on, $trigger } from './events';

export interface YInputOptions {
    required?: boolean;
    disabled?: boolean;
}

export interface YInput<Value> extends YEventSource<YStateEvent & YValueEvent<Value> & YInputEvent> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value>>;

    okay: boolean;
    dirty: boolean;
    disabled: boolean;

    set value(value: Value | undefined);
    get value(): Value;

    markInvalid?(focus: boolean, force?: boolean): boolean;
    focus?(): void;
}

interface FormMapEntry {
    input?: YInput<any>;
    inputs?: YInput<any>[];
}

export type YFormInputSpec = { [slug: string]: YInput<any> | YInput<any>[] }

export class YForm<Value = any> implements YInput<Value> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value>>;
    // undo_provider?: YUndoProvider;

    okay!: boolean;
    private _dirty!: boolean;

    private _inputs: YInput<any>[];
    private _map: { [slug: string]: FormMapEntry };
    private _no_changes?: boolean;

    constructor(inputs: YFormInputSpec) {
        this._inputs = [];
        this._map = {};
        for(const slug in inputs) {
            this._doAdd(slug, inputs[slug]);
        }

        this._recalc();
    }

    private _doAdd(slug: string, input: YInput<any> | YInput<any>[]) {
        const state_handler = () => { this._recalc() };

        if(Array.isArray(input)) {
            for(const i of input) {
                this._inputs.push(i);
                $on(i, 'state', state_handler);
            }

            if(slug != '_')
                this._map[slug] = { inputs: input }
        } else {
            this._inputs.push(input);
            $on(input, 'state', state_handler);

            if(slug != '_')
                this._map[slug] = { input: input };
        }
    }

    add<Input extends YInput<any>>(slug: string, input: Input): Input
    add(inputs: YFormInputSpec): void
    add(slug_inputs: string | YFormInputSpec, input?: YInput<any>) {
        if(typeof slug_inputs === 'string') {
            this._doAdd(slug_inputs, input!);
            this._recalc();
            return input;
        } else {
            for(const slug in slug_inputs) {
                this._doAdd(slug, slug_inputs[slug]);
            }
            this._recalc();
            return;
        }
    }

    validate() {
        if(!this.okay) {
            this.markInvalid(true);
            return false;
        }
        return true;
    }

    get value(): Value {
        const value: { [slug: string]: any } = {};

        const map = this._map;

        for(const s in map) {
            const entry = map[s];
            if(entry.input) {
                value[s] = entry.input.value;
            } else if(entry.inputs) {
                const array: any[] = [];
                for(const i of entry.inputs) {
                    const v = i.value;
                    if(v != undefined)
                        array.push(v);
                }
                value[s] = array;
            }
        }

        return value as Value;
    }

    set value(value: Value) {
        value = value || ({} as Value);

        this._no_changes = true;
        const map = this._map;
        for(const s in map) {
            const v = (value as any)[s];
            const entry = map[s];
            if(entry.input) {
                entry.input.value = v;
            } else if(entry.inputs) {
                for(let i = 0; i < entry.inputs.length; i++) {
                    entry.inputs[i].value = v ? v[i] : undefined;
                }
            }
        }
        this._no_changes = false;
    }

    get dirty() {
        return this._dirty;
    }

    set dirty(d: boolean) {
        if(d === this._dirty) return;
        this._dirty = d;
        if(!d) {
            for(const i of this._inputs) {
                i.dirty = false;
            }
        }
    }

    set disabled(d: boolean) {
        for(const i of this._inputs) {
            i.disabled = d;
        }
    }

    markInvalid(focus: boolean): boolean {
        let focused = false;
        for(const i of this._inputs) {
            if(i.markInvalid)
                focused = i.markInvalid(focus && !focused) || focused;
        }
        return focused;
    }

    focus() {
        for(const i of this._inputs) {
            if(!i.disabled && i.focus) {
                i.focus();
                return;
            }
        }
    }

    isNoChanges() {
        return this._no_changes;
    }

    private _recalc() {
        let changed = false;

        const okay = this._inputs.every(i => i.okay);

        if(okay !== this.okay) {
            changed = true;
            this.okay = okay;
        }

        const dirty = this._inputs.some(i => i.dirty);

        if(dirty !== this._dirty) {
            changed = true;
            this._dirty = dirty;
        }

        if(changed) {
            $trigger(this, 'state');
        }
    }
}

export function $form(inputs: YFormInputSpec) {
    return new YForm(inputs);
}

export namespace $form {
    export function noop(value?: any): YInput<any> {
        return {
            $handlers: undefined,
            okay: true,
            dirty: false,
            disabled: false,
            value: value,
            markInvalid: () => false,
            focus: () => {}
        };
    }

    class Adapter<Src, Dst> implements YInput<Dst> {
        $handlers: YEventHandlers<YStateEvent>;

        private _input: YInput<Src>;
        private _get: (src: Src) => Dst;
        private _set: (dst: Dst) => Src;

        constructor(input: YInput<Src>, get: (src: Src) => Dst, set: (dst: Dst) => Src) {
            this._input = input;
            this._get = get;
            this._set = set;
        }

        get value() {
            return this._get(this._input.value);
        }

        set value(value: Dst) {
            this._input.value = this._set(value);
        }

        get dirty() {
            return this._input.dirty;
        }

        set dirty(d: boolean) {
            this._input.dirty = d;
        }

        get okay() {
            return this._input.okay;
        }

        set okay(o: boolean) {
            this._input.okay = o;
        }

        get disabled() {
            return this._input.disabled;
        }

        set disabled(disabled: boolean) {
            this._input.disabled = disabled;
        }

        markInvalid(focus: boolean): boolean {
            return this._input.markInvalid ? this._input.markInvalid(focus) : false;
        }

        focus() {
            this._input.focus && this._input.focus();
        }
    }

    export function adapt<Src, Dst>(input: YInput<Src>, get: (src: Src) => Dst, set: (dst: Dst) => Src): YInput<Dst> {
        return new Adapter(input, get, set);
    }

    class SimpleInput<Value> implements YInput<Value> {
        $handlers: YEventHandlers<YStateEvent>;

        private _get: () => Value;
        private _set: (v: Value) => void;

        constructor(get: () => Value, set: (v: Value) => void) {
            this._get = get;
            this._set = set;
        }

        get value() {
            return this._get();
        }

        set value(v: Value) {
            this._set(v)
        }

        get dirty() {
            return false;
        }

        set dirty(_d: boolean) {
        }

        get okay() {
            return true;
        }

        set okay(_o: boolean) {
        }

        get disabled() {
            return false;
        }

        set disabled(_disabled: boolean) {
        }

        markInvalid(_focus: boolean): boolean {
            return false;
        }

        focus() {
        }
    }

    export function simple_input<Value>(get: () => Value, set: (v: Value) => void): YInput<Value> {
        return new SimpleInput(get, set);
    }

    class When<Value> implements YInput<Value | undefined> {
        $handlers: YEventHandlers<YStateEvent>;

        private _bool: YInput<boolean>;
        private _input: YInput<Value>;

        okay!: boolean;
        private _dirty!: boolean;

        constructor(bool: YInput<boolean>, input: YInput<Value>) {
            this._bool = bool;
            this._input = input;

            $on(bool, 'value', v => {
                input.disabled = !v;
                this._recalc();
            });

            $on(bool, 'state', () => { this._recalc() });
            $on(input, 'state', () => { this._recalc() });

            input.disabled = !bool.value;
            this._recalc();
        }

        get value() {
            if(this._bool.value)
                return this._input.value;
            else
                return undefined;
        }

        set value(value: Value | undefined) {
            if(value) {
                this._bool.value = true;
                this._input.value = value;
            } else {
                this._bool.value = false;
            }
        }

        get dirty() {
            return this._dirty;
        }

        set dirty(d: boolean) {
            if(d === this._dirty) return;
            this._dirty = d;
            if(!d) {
                this._bool.dirty = false;
                this._input.dirty = false;
            }
        }

        get disabled() {
            return this._input.disabled;
        }

        set disabled(disabled: boolean) {
            this._input.disabled = disabled;
        }

        markInvalid(focus: boolean): boolean {
            if(this._bool.value && this._input.markInvalid)
                return this._input.markInvalid(focus);
            return false;
        }

        focus() {
            this._input.focus && this._input.focus();
        }

        private _recalc() {
            let changed = false;

            const okay = !this._bool.value || this._input.okay;

            if(okay !== this.okay) {
                changed = true;
                this.okay = okay;
            }

            const dirty = this._bool.dirty || (this._bool.value && this._input.dirty);

            if(dirty !== this._dirty) {
                changed = true;
                this._dirty = dirty;
            }

            if(changed) {
                $trigger(this, 'state');
            }
        }
    }

    export function when<Value>(bool: YInput<boolean>, input: YInput<Value>) {
        return new When(bool, input)
    }

    export function setDirty(input: YInput<any>, d: boolean) {
        if(d === input.dirty) return;
        input.dirty = d;
        $trigger(input, 'state');
    }

    export function setOkay(input: YInput<any>, o: boolean) {
        if(o === input.okay) return;
        input.okay = o;
        $trigger(input, 'state');
    }

    export function validate(input: YInput<any> | YInput<any>[]) {
        if(Array.isArray(input)) {
            let okay = true;
            for(const i of input) {
                if(!i.okay) {
                    i.markInvalid && i.markInvalid(okay);
                    okay = false;
                }
            }

            return okay;
        } else if(!input.okay) {
            input.markInvalid && input.markInvalid(true);
            return false;
        }

        return true;
    }
}
